import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { http, loanTypes, notify } from "../api";
import { Loading, Modal, PanelHead } from "./common";

const MANAGE_ROLES = ["admin", "business_head", "back_office_manager"];

export function PoliciesTab({ data, me }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [filterBank, setFilterBank] = useState("");
  const [filterLoanType, setFilterLoanType] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const canManage = MANAGE_ROLES.includes(me?.role);
  const canDelete = me?.role === "admin";

  const load = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (filterBank) params.set("bank_id", filterBank);
    if (filterLoanType) params.set("loan_type", filterLoanType);
    if (!showInactive) params.set("active_only", "true");
    return http
      .get(`/admin/policies?${params.toString()}`)
      .then((r) => setRows(r.data.policies || []))
      .catch((err) => notify.error(err, "Could not load policies"));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive, filterBank, filterLoanType]);

  const removeRow = async (row) => {
    if (!window.confirm(`Delete policy for ${row.bank_name} · ${row.product_name}?`)) return;
    try {
      await http.delete(`/admin/policies/${row.policy_id}`);
      notify.success("Policy deleted");
      load();
    } catch (err) {
      notify.error(err, "Delete failed");
    }
  };

  if (rows === null) return <Loading>Loading policies…</Loading>;

  return (
    <section data-testid="policies-tab-panel">
      <PanelHead
        title="Policies master"
        subtitle="One rulebook per Bank/NBFC + Product. Required documents are inherited from the Bank record."
      />

      {canManage && (
        <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
          <button
            className="primary-button"
            data-testid="policy-add-open"
            onClick={() => setShowAdd(true)}
          >
            <Plus size={14} /> Add policy
          </button>
          <PolicyBulkImport banks={data.banks} products={data.products} onDone={load} />
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", margin: "12px 0" }}>
        <input
          data-testid="policies-search"
          placeholder="Search bank / product / loan type…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          style={{ padding: "8px 12px", border: "1px solid var(--border)", background: "var(--surface)", minWidth: 240 }}
        />
        <select data-testid="policies-filter-bank" value={filterBank} onChange={(e) => setFilterBank(e.target.value)}>
          <option value="">All banks/NBFCs</option>
          {(data.banks || []).map((b) => (
            <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
          ))}
        </select>
        <select data-testid="policies-filter-loantype" value={filterLoanType} onChange={(e) => setFilterLoanType(e.target.value)}>
          <option value="">All loan types</option>
          {loanTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <button className="outline-button" onClick={load}>Search</button>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--muted)" }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive
        </label>
        <span className="muted" style={{ fontSize: 12 }}>{rows.length} shown</span>
      </div>

      <div className="table-wrap">
        <table data-testid="policies-table">
          <thead>
            <tr>
              <th>Bank / NBFC</th>
              <th>Product</th>
              <th>Loan type</th>
              <th>Income (min)</th>
              <th>Max loan</th>
              <th>ROI</th>
              <th>Tenure (mo)</th>
              <th>Categories</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="muted" style={{ padding: 16 }}>No policies match — add one above.</td></tr>
            )}
            {rows.map((row) => (
              <tr key={row.policy_id} data-testid={`policy-row-${row.policy_id}`}>
                <td>{row.bank_name}</td>
                <td>{row.product_name}</td>
                <td>{row.loan_type || "—"}</td>
                <td>{row.min_income ? `₹${Number(row.min_income).toLocaleString("en-IN")}` : "—"}</td>
                <td>{row.max_loan_amount ? `₹${Number(row.max_loan_amount).toLocaleString("en-IN")}` : "—"}</td>
                <td>{row.roi_min || row.roi_max ? `${row.roi_min}% – ${row.roi_max}%` : "—"}</td>
                <td>{row.tenure_min_months || row.tenure_max_months ? `${row.tenure_min_months} – ${row.tenure_max_months}` : "—"}</td>
                <td>{(row.company_categories || []).join(", ") || "All"}</td>
                <td>
                  <span className={row.active ? "chip chip-green" : "chip chip-muted"}>
                    {row.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ textAlign: "right" }}>
                  {canManage && (
                    <button
                      className="icon-button"
                      data-testid={`policy-edit-${row.policy_id}`}
                      onClick={() => setEditing(row)}
                      aria-label="Edit"
                    >
                      <Pencil size={14} />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      className="icon-button danger"
                      data-testid={`policy-delete-${row.policy_id}`}
                      onClick={() => removeRow(row)}
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <PolicyEditModal
          banks={data.banks}
          products={data.products}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
      {editing && (
        <PolicyEditModal
          policy={editing}
          banks={data.banks}
          products={data.products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}

function PolicyEditModal({ policy, banks, products, onClose, onSaved }) {
  const editing = !!policy;
  const [form, setForm] = useState({
    bank_id: policy?.bank_id || "",
    product_id: policy?.product_id || "",
    loan_type: policy?.loan_type || "",
    min_income: policy?.min_income || 0,
    max_loan_amount: policy?.max_loan_amount || 0,
    min_age: policy?.min_age || 21,
    max_age: policy?.max_age || 60,
    roi_min: policy?.roi_min || 0,
    roi_max: policy?.roi_max || 0,
    tenure_min_months: policy?.tenure_min_months || 12,
    tenure_max_months: policy?.tenure_max_months || 60,
    employment_types: policy?.employment_types || ["Salaried"],
    company_categories: (policy?.company_categories || []).join(", "),
    eligibility_notes: policy?.eligibility_notes || "",
    active: policy?.active !== false,
  });
  const [busy, setBusy] = useState(false);

  const bankProducts = useMemo(
    () => (products || []).filter((p) => p.bank_id === form.bank_id),
    [products, form.bank_id]
  );

  const toggleEmploymentType = (t) => {
    setForm((f) => ({
      ...f,
      employment_types: f.employment_types.includes(t)
        ? f.employment_types.filter((x) => x !== t)
        : [...f.employment_types, t],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_id || !form.product_id) {
      notify.error(null, "Please pick a bank and product.");
      return;
    }
    setBusy(true);
    const payload = {
      bank_id: form.bank_id,
      product_id: form.product_id,
      loan_type: form.loan_type,
      min_income: Number(form.min_income) || 0,
      max_loan_amount: Number(form.max_loan_amount) || 0,
      min_age: Number(form.min_age) || 0,
      max_age: Number(form.max_age) || 0,
      roi_min: Number(form.roi_min) || 0,
      roi_max: Number(form.roi_max) || 0,
      tenure_min_months: Number(form.tenure_min_months) || 0,
      tenure_max_months: Number(form.tenure_max_months) || 0,
      employment_types: form.employment_types,
      company_categories: form.company_categories
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
      eligibility_notes: form.eligibility_notes,
      active: form.active,
    };
    try {
      if (editing) {
        await http.patch(`/admin/policies/${policy.policy_id}`, payload);
        notify.success("Policy updated");
      } else {
        await http.post("/admin/policies", payload);
        notify.success("Policy added");
      }
      onSaved();
    } catch (err) {
      notify.error(err, "Could not save policy");
      setBusy(false);
    }
  };

  return (
    <Modal title={editing ? `Edit policy` : "Add policy"} onClose={onClose}>
      <form className="record-form" onSubmit={submit} data-testid="policy-edit-form">
        <div className="form-grid">
          <label>
            Bank / NBFC
            <select
              data-testid="policy-edit-bank"
              value={form.bank_id}
              onChange={(e) => setForm({ ...form, bank_id: e.target.value, product_id: "" })}
              required
              disabled={editing}
            >
              <option value="">— Select —</option>
              {(banks || []).filter((b) => b.active !== false).map((b) => (
                <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label>
            Product
            <select
              data-testid="policy-edit-product"
              value={form.product_id}
              onChange={(e) => {
                const p = bankProducts.find((x) => x.product_id === e.target.value);
                setForm({ ...form, product_id: e.target.value, loan_type: p?.loan_type || form.loan_type });
              }}
              required
              disabled={editing || !form.bank_id}
            >
              <option value="">— Select —</option>
              {bankProducts.filter((p) => p.active !== false).map((p) => (
                <option key={p.product_id} value={p.product_id}>{p.name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="form-grid">
          <label>
            Loan type
            <select
              data-testid="policy-edit-loantype"
              value={form.loan_type}
              onChange={(e) => setForm({ ...form, loan_type: e.target.value })}
            >
              <option value="">— Select —</option>
              {loanTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>
            Status
            <select
              data-testid="policy-edit-active"
              value={form.active ? "active" : "inactive"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "active" })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
        </div>

        <div className="form-grid">
          <label>
            Min monthly income (₹)
            <input type="number" min="0" step="1000" data-testid="policy-edit-min-income"
              value={form.min_income} onChange={(e) => setForm({ ...form, min_income: e.target.value })} />
          </label>
          <label>
            Max loan amount (₹)
            <input type="number" min="0" step="10000" data-testid="policy-edit-max-loan"
              value={form.max_loan_amount} onChange={(e) => setForm({ ...form, max_loan_amount: e.target.value })} />
          </label>
        </div>

        <div className="form-grid">
          <label>
            Min age
            <input type="number" min="18" max="80" data-testid="policy-edit-min-age"
              value={form.min_age} onChange={(e) => setForm({ ...form, min_age: e.target.value })} />
          </label>
          <label>
            Max age
            <input type="number" min="18" max="80" data-testid="policy-edit-max-age"
              value={form.max_age} onChange={(e) => setForm({ ...form, max_age: e.target.value })} />
          </label>
        </div>

        <div className="form-grid">
          <label>
            ROI min (%)
            <input type="number" min="0" step="0.05" data-testid="policy-edit-roi-min"
              value={form.roi_min} onChange={(e) => setForm({ ...form, roi_min: e.target.value })} />
          </label>
          <label>
            ROI max (%)
            <input type="number" min="0" step="0.05" data-testid="policy-edit-roi-max"
              value={form.roi_max} onChange={(e) => setForm({ ...form, roi_max: e.target.value })} />
          </label>
        </div>

        <div className="form-grid">
          <label>
            Tenure min (months)
            <input type="number" min="0" data-testid="policy-edit-tenure-min"
              value={form.tenure_min_months} onChange={(e) => setForm({ ...form, tenure_min_months: e.target.value })} />
          </label>
          <label>
            Tenure max (months)
            <input type="number" min="0" data-testid="policy-edit-tenure-max"
              value={form.tenure_max_months} onChange={(e) => setForm({ ...form, tenure_max_months: e.target.value })} />
          </label>
        </div>

        <label>
          Employment types
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
            {["Salaried", "Self-Employed", "Business Owner", "Professional"].map((t) => (
              <label key={t} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, margin: 0 }}>
                <input
                  type="checkbox"
                  data-testid={`policy-edit-emp-${t}`}
                  checked={form.employment_types.includes(t)}
                  onChange={() => toggleEmploymentType(t)}
                />
                {t}
              </label>
            ))}
          </div>
        </label>

        <label>
          Eligible company categories (comma-separated, leave blank = all)
          <input
            data-testid="policy-edit-categories"
            value={form.company_categories}
            onChange={(e) => setForm({ ...form, company_categories: e.target.value })}
            placeholder="e.g. A, B, Cat 1"
          />
        </label>

        <label>
          Eligibility notes
          <textarea
            data-testid="policy-edit-notes"
            rows={3}
            value={form.eligibility_notes}
            onChange={(e) => setForm({ ...form, eligibility_notes: e.target.value })}
            placeholder="e.g. Min 6 months in current job, CIBIL 700+"
          />
        </label>

        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy} data-testid="policy-edit-save">
            {busy ? "Saving…" : editing ? "Save changes" : "Add policy"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PolicyBulkImport({ banks, products, onDone }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = () => {
    const header = "Bank Name,Product Name,Loan Type,Min Income,Max Loan Amount,Min Age,Max Age,ROI Min,ROI Max,Tenure Min Months,Tenure Max Months,Employment Types,Company Categories,Eligibility Notes,Active";
    const sample = "HDFC Bank,Personal Loan,Personal Loan,25000,2500000,21,60,10.5,18.5,12,72,Salaried|Self-Employed,A|B,Min 6 months at current job,true";
    const csv = header + "\n" + sample + "\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "policies-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const parseCsv = (text) => {
    const bankMap = new Map((banks || []).map((b) => [b.name.toLowerCase(), b]));
    const productMap = new Map(
      (products || []).map((p) => [`${p.bank_id}::${p.name.toLowerCase()}`, p])
    );
    const out = [];
    const errs = [];
    const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
    if (!lines.length) return { rows: [], errors: ["File is empty."] };
    let start = 0;
    if (lines[0].toLowerCase().includes("bank") && lines[0].toLowerCase().includes("product")) start = 1;
    for (let i = start; i < lines.length; i++) {
      const parts = [];
      let cur = ""; let inQuotes = false;
      const raw = lines[i];
      for (let ch = 0; ch < raw.length; ch++) {
        const c = raw[ch];
        if (c === '"') { inQuotes = !inQuotes; continue; }
        if (c === "," && !inQuotes) { parts.push(cur); cur = ""; continue; }
        cur += c;
      }
      parts.push(cur);
      const bankName = (parts[0] || "").trim();
      const productName = (parts[1] || "").trim();
      const bank = bankMap.get(bankName.toLowerCase());
      if (!bank) {
        errs.push(`Line ${i + 1}: Bank '${bankName}' not found — skipped.`);
        continue;
      }
      const product = productMap.get(`${bank.bank_id}::${productName.toLowerCase()}`);
      if (!product) {
        errs.push(`Line ${i + 1}: Product '${productName}' not found under '${bankName}' — skipped.`);
        continue;
      }
      out.push({
        bank_id: bank.bank_id,
        product_id: product.product_id,
        loan_type: (parts[2] || "").trim(),
        min_income: Number(parts[3] || 0) || 0,
        max_loan_amount: Number(parts[4] || 0) || 0,
        min_age: Number(parts[5] || 0) || 0,
        max_age: Number(parts[6] || 0) || 0,
        roi_min: Number(parts[7] || 0) || 0,
        roi_max: Number(parts[8] || 0) || 0,
        tenure_min_months: Number(parts[9] || 0) || 0,
        tenure_max_months: Number(parts[10] || 0) || 0,
        employment_types: (parts[11] || "").split("|").map((s) => s.trim()).filter(Boolean),
        company_categories: (parts[12] || "").split("|").map((s) => s.trim()).filter(Boolean),
        eligibility_notes: (parts[13] || "").trim(),
        active: String(parts[14] || "true").trim().toLowerCase() !== "false",
      });
    }
    return { rows: out, errors: errs };
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ""));
      setRows(parsed.rows);
      setErrors(parsed.errors);
    };
    reader.readAsText(f);
  };

  const upload = async () => {
    if (!rows.length || busy) return;
    setBusy(true);
    try {
      const r = await http.post("/admin/policies/bulk", rows);
      notify.success(`Imported ${r.data.inserted} · skipped ${r.data.skipped}`);
      setFile(null); setRows([]); setErrors([]);
      onDone();
    } catch (err) {
      notify.error(err, "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }} data-testid="policies-bulk-import">
      <button
        type="button"
        className="outline-button"
        data-testid="policies-bulk-download-template"
        onClick={downloadTemplate}
      >
        Download template
      </button>
      <input
        type="file"
        accept=".csv,text/csv"
        data-testid="policies-bulk-file"
        onChange={onFile}
        style={{ fontSize: 12 }}
      />
      <button
        type="button"
        className="primary-button"
        data-testid="policies-bulk-upload"
        disabled={!rows.length || busy}
        onClick={upload}
      >
        {busy ? "Importing…" : rows.length ? `Import ${rows.length}` : "Import"}
      </button>
      {errors.length > 0 && (
        <span className="muted" style={{ fontSize: 11, color: "var(--amber)" }}>
          {errors.length} skipped
        </span>
      )}
    </div>
  );
}
